import { useMemo, useState } from 'react'
import {
  ORDER_STATUS_FILTERS,
  ORDERS_PAGE,
  type OrderStatus,
  type VendorOrderDetailProps,
} from '../constants/orders'
import { formatCurrency, formatDateTime } from '../helpers/format'
import {
  checkoutRefLabel,
  deliveryPromiseLabel,
  formatBillingLine,
  formatDeliveryLine,
  orderStatusLabel,
} from '../helpers/orders'
import { Modal } from '../components/Modal'
import { PageLoader } from '../components/PageLoader'
import { Spinner } from '../components/Spinner'
import { Topbar } from '../components/Topbar'
import { LOADING_LABELS } from '../constants/loading'
import { useOrders } from '../hooks/useOrders'
import '../styles/catalog-page.scss'
import '../styles/catalog-form.scss'
import '../styles/vendor-table.scss'
import '../styles/orders-page.scss'

export function OrdersPage() {
  const {
    items,
    loading,
    loadError,
    statusFilter,
    setStatusFilter,
    selected,
    setSelected,
    error,
    openingId,
    openOrder,
  } = useOrders()
  const [query, setQuery] = useState('')
  const [isOpen, setOpen] = useState(false)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return items
    return items.filter((order) => {
      const haystack = [
        order.orderNumber,
        order.delivery.recipientName,
        order.delivery.city,
      ]
        .join(' ')
        .toLowerCase()
      return haystack.includes(q)
    })
  }, [items, query])

  async function handleOpen(id: string) {
    setOpen(true)
    await openOrder(id)
  }

  return (
    <div className="catalog-page">
      <Topbar title={ORDERS_PAGE.title} subtitle={ORDERS_PAGE.subtitle} />
      {loadError ? <p className="form-error">{loadError}</p> : null}
      {loading ? (
        <PageLoader />
      ) : (
        <section className="panel">
          <div className="panel__header">
            <h2 className="panel__title">
              Orders ({filtered.length}
              {filtered.length !== items.length ? ` / ${items.length}` : ''})
            </h2>
            <div className="panel__tools">
              <label className="search-field">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-3.5-3.5" />
                </svg>
                <input
                  type="search"
                  placeholder={ORDERS_PAGE.searchPlaceholder}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
              </label>
              <label className="orders-filter">
                <span className="sr-only">Status</span>
                <select
                  value={statusFilter}
                  onChange={(event) =>
                    setStatusFilter(event.target.value as 'all' | OrderStatus)
                  }
                >
                  {ORDER_STATUS_FILTERS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {items.length === 0 ? (
            <p className="table-empty">
              {statusFilter === 'all' ? ORDERS_PAGE.empty : ORDERS_PAGE.emptyFilter}
            </p>
          ) : filtered.length === 0 ? (
            <p className="table-empty">{ORDERS_PAGE.emptyFilter}</p>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Recipient</th>
                    <th>City</th>
                    <th>SLA</th>
                    <th>Status</th>
                    <th>Total</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((order) => {
                    const checkoutRef = checkoutRefLabel(order.checkoutId)
                    return (
                    <tr key={order.id}>
                      <td>
                        <p className="orders-cell__id">{order.orderNumber}</p>
                        <p className="cell-muted">{formatDateTime(order.createdAt)}</p>
                        {checkoutRef ? <p className="cell-muted">{checkoutRef}</p> : null}
                      </td>
                      <td>{order.delivery.recipientName}</td>
                      <td className="cell-muted">{order.delivery.city}</td>
                      <td>
                        <span
                          className={`order-status${order.isExpress ? ' order-status--delivered' : ''}`}
                        >
                          {deliveryPromiseLabel(order.isExpress)}
                        </span>
                      </td>
                      <td>
                        <span className={`order-status order-status--${order.status}`}>
                          {orderStatusLabel(order.status)}
                        </span>
                      </td>
                      <td>{formatCurrency(order.total)}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          disabled={openingId === order.id}
                          onClick={() => {
                            void handleOpen(order.id)
                          }}
                        >
                          {openingId === order.id ? (
                            <Spinner inline label={LOADING_LABELS.opening} />
                          ) : (
                            'View'
                          )}
                        </button>
                      </td>
                    </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <Modal
        title={selected?.orderNumber ?? ORDERS_PAGE.detailTitle}
        open={isOpen}
        onClose={() => {
          setOpen(false)
          setSelected(null)
        }}
      >
        {selected ? (
          <VendorOrderDetail order={selected} error={error} />
        ) : (
          <div className="catalog-form">
            <Spinner block label={LOADING_LABELS.opening} />
          </div>
        )}
      </Modal>
    </div>
  )
}

function VendorOrderDetail({ order, error }: VendorOrderDetailProps) {
  const checkoutRef = checkoutRefLabel(order.checkoutId)
  return (
    <div className="catalog-form order-detail">
      <div className="order-detail__status">
        <span className={`order-status order-status--${order.status}`}>
          {orderStatusLabel(order.status)}
        </span>
        <span className="cell-muted">{order.orderNumber}</span>
        {checkoutRef ? <span className="cell-muted">{checkoutRef}</span> : null}
        <span
          className={`order-status${order.isExpress ? ' order-status--delivered' : ''}`}
        >
          {deliveryPromiseLabel(order.isExpress)}
        </span>
      </div>
      <section className="form-group">
        <div className="form-group__header">
          <p className="form-group__label">{ORDERS_PAGE.itemsHeading}</p>
        </div>
        <ul className="order-detail__items">
          {order.items.map((item) => (
            <li key={item.id}>
              <span>
                {item.productName} · {item.variantLabel} × {item.quantity}
              </span>
              <strong>{formatCurrency(item.lineTotal)}</strong>
            </li>
          ))}
        </ul>
        <p className="order-detail__total">
          Total <strong>{formatCurrency(order.total)}</strong>
        </p>
      </section>
      <section className="form-group">
        <div className="form-group__header">
          <p className="form-group__label">{ORDERS_PAGE.deliveryHeading}</p>
        </div>
        <p>
          {order.delivery.recipientName} · {order.delivery.recipientPhone}
        </p>
        <p className="cell-muted">{formatDeliveryLine(order)}</p>
        {order.delivery.notes ? <p>{order.delivery.notes}</p> : null}
      </section>
      <section className="form-group">
        <div className="form-group__header">
          <p className="form-group__label">{ORDERS_PAGE.billingHeading}</p>
        </div>
        {order.billing.sameAsShipping ? (
          <p>{ORDERS_PAGE.billingSameAsShipping}</p>
        ) : null}
        <p>
          {order.billing.name} · {order.billing.phone}
        </p>
        <p className="cell-muted">{formatBillingLine(order)}</p>
      </section>
      {error ? <p className="form-error">{error}</p> : null}
    </div>
  )
}
